import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listLenses, upsertLens, createCustomLens, deleteLens } from "@/lib/phase3_api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, PencilSimple, ArrowClockwise, Trash, FloppyDisk, X, WarningCircle,
  Share as ShareIcon, UploadSimple,
} from "@phosphor-icons/react";
import { ShareLensDialog, ImportLensDialog } from "@/components/LensShare";

const EMPTY = {
  vertical: "",
  aliases: [],
  buyer_concerns: [],
  compliance: [],
  integration_expectations: [],
  security_data_sensitivity: "",
  churn_drivers: [],
  anti_patterns: [],
};

function ArrayField({ label, values, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v) {
      onChange([...(values || []), v]);
      setDraft("");
    }
  };
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
        {label} · {(values || []).length}
      </div>
      <div className="space-y-1 mb-2">
        {(values || []).map((v, i) => (
          <div key={i} className="flex items-center gap-2 border border-zinc-200 bg-white px-3 py-1.5">
            <span className="flex-1 text-sm text-zinc-800 break-words">{v}</span>
            <button
              type="button"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              className="text-zinc-400 hover:text-red-600"
              data-testid={`${testid}-remove-${i}`}
              aria-label="remove"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-1">
        <Input
          data-testid={`${testid}-input`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="sharp border-zinc-300 font-mono text-xs h-8"
        />
        <button
          type="button"
          onClick={add}
          data-testid={`${testid}-add`}
          className="border border-zinc-950 text-zinc-950 px-3 h-8 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-100"
        >
          Add
        </button>
      </div>
    </div>
  );
}

function LensEditor({ entry, onSaved, onCancel }) {
  const [form, setForm] = useState(() => ({ ...entry.lens }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.vertical.trim()) {
      toast.error("Vertical name is required");
      return;
    }
    try {
      setSaving(true);
      if (entry.is_seeded) {
        await upsertLens(form.vertical, form);
      } else if (entry.is_custom) {
        await upsertLens(form.vertical, form);
      } else {
        await createCustomLens(form);
      }
      toast.success("Lens saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-zinc-950 bg-white p-6" data-testid="lens-editor">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {entry.is_seeded ? (entry.is_overridden ? "Editing (overridden seeded lens)" : "Editing seeded lens") : entry.is_custom ? "Editing custom lens" : "Creating new custom lens"}
          </div>
          <Input
            data-testid="lens-vertical-input"
            value={form.vertical}
            onChange={(e) => setForm({ ...form, vertical: e.target.value })}
            disabled={entry.is_seeded || entry.is_custom}
            className="sharp mt-1 font-display text-2xl border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-auto p-0 tracking-tight"
            placeholder="Vertical name (e.g. Legal Tech)"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="border border-zinc-300 px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-100"
            data-testid="lens-cancel"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="lens-save"
            className="bg-zinc-950 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-2"
          >
            <FloppyDisk size={12} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ArrayField label="Buyer concerns" values={form.buyer_concerns} onChange={(v) => setForm({ ...form, buyer_concerns: v })} placeholder="e.g. Audit trails" testid="buyer-concerns" />
        <ArrayField label="Compliance / regulatory" values={form.compliance} onChange={(v) => setForm({ ...form, compliance: v })} placeholder="e.g. SOC 2" testid="compliance" />
        <ArrayField label="Expected integrations" values={form.integration_expectations} onChange={(v) => setForm({ ...form, integration_expectations: v })} placeholder="e.g. Stripe" testid="integrations" />
        <ArrayField label="Churn drivers" values={form.churn_drivers} onChange={(v) => setForm({ ...form, churn_drivers: v })} placeholder="e.g. Failed reconciliation" testid="churn" />
        <ArrayField label="Anti-patterns" values={form.anti_patterns} onChange={(v) => setForm({ ...form, anti_patterns: v })} placeholder="e.g. Gamification" testid="anti" />
        <ArrayField label="Aliases (fuzzy match)" values={form.aliases} onChange={(v) => setForm({ ...form, aliases: v })} placeholder="e.g. fintech" testid="aliases" />
      </div>

      <div className="mt-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
          Security / data sensitivity
        </div>
        <Textarea
          data-testid="security-input"
          value={form.security_data_sensitivity || ""}
          onChange={(e) => setForm({ ...form, security_data_sensitivity: e.target.value })}
          rows={2}
          placeholder="e.g. Very high — handles money, PII, and tax-relevant records."
          className="sharp border-zinc-300 font-mono text-sm resize-none"
        />
      </div>
    </div>
  );
}

function LensRow({ entry, onEdit, onDelete, onShare }) {
  const lens = entry.lens;
  const badge = entry.is_seeded
    ? entry.is_overridden
      ? <span className="tag-inferred tag-confidence-medium">seeded · overridden</span>
      : <span className="tag-observed">seeded</span>
    : <span className="tag-observed" style={{ background: "#2563EB", borderColor: "#2563EB" }}>custom</span>;
  return (
    <div className="grid grid-cols-[220px_1fr_auto] gap-4 items-center border-b border-zinc-200 px-5 py-3 hover:bg-zinc-50" data-testid={`lens-row-${lens.vertical}`}>
      <div>
        <div className="font-display font-medium text-zinc-950">{lens.vertical}</div>
        <div className="mt-1">{badge}</div>
      </div>
      <div className="text-xs text-zinc-600 line-clamp-2">
        {(lens.buyer_concerns || []).slice(0, 3).join(" · ") || <span className="text-zinc-400">no concerns set</span>}
      </div>
      <div className="flex gap-1">
        <button
          onClick={() => onShare(entry)}
          data-testid={`lens-share-${lens.vertical}`}
          className="border border-zinc-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-white flex items-center gap-1.5 text-zinc-800"
        >
          <ShareIcon size={11} /> Share
        </button>
        <button
          onClick={() => onEdit(entry)}
          data-testid={`lens-edit-${lens.vertical}`}
          className="border border-zinc-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-white flex items-center gap-1.5"
        >
          <PencilSimple size={11} /> Edit
        </button>
        {(entry.is_overridden || entry.is_custom) && (
          <button
            onClick={() => onDelete(entry)}
            data-testid={`lens-delete-${lens.vertical}`}
            className="border border-zinc-300 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-white flex items-center gap-1.5 text-zinc-800"
          >
            {entry.is_custom ? <Trash size={11} /> : <ArrowClockwise size={11} />}
            {entry.is_custom ? "Delete" : "Reset"}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Lenses() {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({ queryKey: ["lenses"], queryFn: listLenses });
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sharingVertical, setSharingVertical] = useState(null);
  const [importing, setImporting] = useState(false);

  const doDelete = async (entry) => {
    try {
      await deleteLens(entry.lens.vertical);
      toast.success(entry.is_custom ? "Custom lens deleted" : "Seeded lens reset to default");
      qc.invalidateQueries({ queryKey: ["lenses"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Delete failed");
    }
  };

  const onSaved = () => {
    setEditing(null);
    setCreating(false);
    qc.invalidateQueries({ queryKey: ["lenses"] });
  };

  return (
    <div className="flex-1 min-h-screen" data-testid="lenses-page">
      <header className="border-b border-zinc-200 bg-white px-6 lg:px-12 py-6">
        <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-2">
          <ArrowLeft size={12} /> New analysis
        </Link>
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Configuration</div>
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950">Industry Lenses</h1>
            <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
              Every analysis is weighted through a lens. Edit the 10 seeded lenses to fit your worldview, or add your own for niche verticals.
              Seeded lenses can be reset back to default at any time.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setImporting(true)}
              data-testid="import-lens-button"
              className="border border-zinc-300 px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-100 flex items-center gap-2"
            >
              <UploadSimple size={12} /> Import JSON
            </button>
            <button
              onClick={() => { setCreating(true); setEditing({ lens: { ...EMPTY }, is_seeded: false, is_custom: false, is_overridden: false }); }}
              data-testid="new-lens-button"
              className="bg-zinc-950 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800 flex items-center gap-2"
            >
              <Plus size={12} /> New custom lens
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8 space-y-6">
        {editing && (
          <LensEditor entry={{ ...editing, is_custom: creating ? false : editing.is_custom, is_seeded: creating ? false : editing.is_seeded }} onSaved={onSaved} onCancel={() => { setEditing(null); setCreating(false); }} />
        )}

        <div className="border border-zinc-200 bg-white">
          <div className="px-5 py-3 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-50 flex items-center justify-between">
            <span>All lenses · {items.length}</span>
            {items.some(i => i.is_overridden || i.is_custom) && (
              <span className="inline-flex items-center gap-1 text-amber-700">
                <WarningCircle size={11} /> some lenses are customised
              </span>
            )}
          </div>
          {isLoading ? (
            <div className="p-6 text-sm text-zinc-500 font-mono">Loading lenses…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500">No lenses.</div>
          ) : (
            items.map((entry) => (
              <LensRow
                key={entry.lens.vertical}
                entry={entry}
                onEdit={(e) => { setEditing(e); setCreating(false); }}
                onDelete={doDelete}
                onShare={(e) => setSharingVertical(e.lens.vertical)}
              />
            ))
          )}
        </div>
      </div>

      <ShareLensDialog
        vertical={sharingVertical}
        open={!!sharingVertical}
        onOpenChange={(v) => !v && setSharingVertical(null)}
      />
      <ImportLensDialog
        open={importing}
        onOpenChange={setImporting}
        onImported={() => qc.invalidateQueries({ queryKey: ["lenses"] })}
      />
    </div>
  );
}
